import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import PasswordChangeModal from '../components/PasswordChangeModal';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const profileSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('Fornavn er påkrevd')
    .min(2, 'Fornavn må være minst 2 tegn')
    .max(50, 'Fornavn kan ikke være mer enn 50 tegn'),
  lastName: yup
    .string()
    .required('Etternavn er påkrevd')
    .min(2, 'Etternavn må være minst 2 tegn')
    .max(50, 'Etternavn kan ikke være mer enn 50 tegn'),
  email: yup
    .string()
    .required('E-post er påkrevd')
    .email('Ugyldig e-postadresse'),
  phone: yup
    .string()
    .required('Telefonnummer er påkrevd')
    .min(8, 'Telefonnummer må være minst 8 tegn')
    .max(15, 'Telefonnummer kan ikke være mer enn 15 tegn')
    .matches(/^[\d\s\+\-\(\)]+$/, 'Telefonnummer kan kun inneholde tall, mellomrom og tegn: + - ( )'),
});

const UserProfile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || ''
    }
  });

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        updateUser(result.user);
        setMessage({ type: 'success', text: 'Profil oppdatert!' });
        
        // Reset form with new values
        reset({
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          email: result.user.email,
          phone: result.user.phone
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Feil ved oppdatering av profil');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Feil ved oppdatering av profil' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChangeSuccess = () => {
    setShowPasswordModal(false);
    setMessage({ type: 'success', text: 'Passord endret!' });
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="user-profile-page">
      <Header />
      
      <div className="user-profile-content">
        <div className="user-profile-card">
          <div className="profile-header">
            <h1>Min profil</h1>
            <p>Oppdater din personlige informasjon</p>
          </div>

          {message && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="profile-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName" className="required">
                  Fornavn
                </label>
                <input
                  {...register('firstName')}
                  type="text"
                  id="firstName"
                  className={errors.firstName ? 'error' : ''}
                  disabled={isLoading}
                />
                {errors.firstName && (
                  <span className="error-message">{errors.firstName.message}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="lastName" className="required">
                  Etternavn
                </label>
                <input
                  {...register('lastName')}
                  type="text"
                  id="lastName"
                  className={errors.lastName ? 'error' : ''}
                  disabled={isLoading}
                />
                {errors.lastName && (
                  <span className="error-message">{errors.lastName.message}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="required">
                  E-post
                </label>
                <input
                  {...register('email')}
                  type="email"
                  id="email"
                  className={errors.email ? 'error' : ''}
                  disabled={isLoading}
                />
                {errors.email && (
                  <span className="error-message">{errors.email.message}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="required">
                  Telefonnummer
                </label>
                <input
                  {...register('phone')}
                  type="tel"
                  id="phone"
                  className={errors.phone ? 'error' : ''}
                  disabled={isLoading}
                  placeholder="+47 123 45 678"
                />
                {errors.phone && (
                  <span className="error-message">{errors.phone.message}</span>
                )}
              </div>
            </div>

            <div className="profile-info-section">
              <h3>Kontoinformasjon</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Brukernavn</label>
                  <span>{user.username}</span>
                </div>
                <div className="info-item">
                  <label>Rolle</label>
                  <span className={`badge badge-${user.role}`}>{user.role}</span>
                </div>
                <div className="info-item">
                  <label>Avdeling</label>
                  <span className={`badge badge-${user.department}`}>{user.department}</span>
                </div>
                <div className="info-item">
                  <label>Avis</label>
                  <span>{user.newspaper}</span>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPasswordModal(true)}
                disabled={isLoading}
              >
                Endre passord
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
        isFirstLogin={false}
      />
    </div>
  );
};

export default UserProfile;